import { Link, NavLink } from 'react-router-dom';
import { Search, ShoppingBag, Heart, User } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { usePlatform } from '@/providers/PlatformProvider';
import { useCart } from '@/providers/CartProvider';
import { PRIMARY_LINKS, visibleLinks } from '@/config/navigation';

/**
 * Desktop header — BUILD-PLAN §26 ("desktop should use an elegant header
 * navigation system instead") and §56 public page structure.
 *
 * Navigation is derived from feature state, not hard-coded, so switching a
 * module off removes its entry everywhere at once. The link list itself now
 * lives in `config/navigation` because the footer and the mobile menu render
 * the same destinations — see the note there.
 */

export function Header() {
  const { isVisible } = usePlatform();
  const { itemCount, open } = useCart();

  const links = visibleLinks(PRIMARY_LINKS, isVisible);

  return (
    <header className="sticky top-0 z-30 border-b border-line bg-background/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-4 sm:px-6 lg:px-8">
        <Link to="/" aria-label="Glanz Luxe — home" className="shrink-0">
          <Logo size="sm" decorative />
        </Link>

        {/*
          Shown from `lg`, not `md`.
          ------------------------------------------------------------------
          Seven multi-word links plus a logo and four icons need about a
          thousand pixels. At 834px they wrapped onto THREE lines and shoved
          the cart icon off the right edge of the page. `lg` is where the bar
          actually fits; below it, BottomNavigation takes over — and its
          breakpoint is tied to this one, so the band between them can never
          end up with no navigation at all.
        */}
        <nav aria-label="Primary" className="hidden lg:block">
          <ul className="flex items-center gap-6 xl:gap-7">
            {links.map(({ to, label }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    [
                      // Never wrap: a nav link broken across two lines makes
                      // the whole bar taller and reads as a layout fault.
                      'whitespace-nowrap text-sm tracking-wide transition-colors',
                      isActive
                        ? 'text-accent'
                        : 'text-ink hover:text-accent',
                    ].join(' ')
                  }
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex shrink-0 items-center gap-1">
          {isVisible('store') && (
            <IconLink to="/search" label="Search">
              <Search className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
            </IconLink>
          )}

          {/* Wishlist and account icons are hidden on mobile, where the same
              destinations live in the bottom navigation. */}
          {isVisible('wishlist') && (
            <IconLink to="/wishlist" label="Wishlist" desktopOnly>
              <Heart className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
            </IconLink>
          )}

          {isVisible('customer_accounts') && (
            <IconLink to="/account" label="Account" desktopOnly>
              <User className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
            </IconLink>
          )}

          {/* §56/§63: the cart stays reachable at every size, and opens the
              drawer rather than navigating away from the current page. */}
          {isVisible('cart') && (
            <button
              type="button"
              onClick={open}
              aria-label={itemCount > 0 ? `Cart, ${itemCount} items` : 'Cart'}
              className="relative flex h-11 w-11 items-center justify-center rounded-sm text-ink transition-colors hover:text-accent"
            >
              <ShoppingBag className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />

              {itemCount > 0 && (
                <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[0.625rem] text-white">
                  {itemCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

/** 44px minimum touch target per §27 / WCAG 2.2 AA. */
function IconLink({
  to,
  label,
  children,
  desktopOnly = false,
}: {
  to: string;
  label: string;
  children: React.ReactNode;
  desktopOnly?: boolean;
}) {
  return (
    <Link
      to={to}
      aria-label={label}
      className={`flex h-11 w-11 items-center justify-center rounded-sm text-ink transition-colors hover:text-accent ${
        desktopOnly ? 'hidden lg:flex' : ''
      }`}
    >
      {children}
    </Link>
  );
}
